import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { api, useAxiosInterceptor } from "./axiosInstance";
import MfaSetup from "./MfaSetup";
import UpdatePassword from "./UpdatePassword";
import SmsDigitInput from "./SmsDigitInput";
import { captureUtmToLocalStorage } from "../../utils/utmStorage";

const SignIn = ({ onSignInSuccess }) => {
    const navigate = useNavigate();
    useAxiosInterceptor();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    // MFA State
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState("");
    const [tempUserId, setTempUserId] = useState(null);
    const [mfaMethod, setMfaMethod] = useState('totp'); // 'totp' | 'sms'
    const [maskedPhone, setMaskedPhone] = useState('');
    const [smsLoginSending, setSmsLoginSending] = useState(false);

    // MFA Setup State (for Company Admins without MFA)
    const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
    const [pendingLoginData, setPendingLoginData] = useState(null);

    // Password update state
    const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
    const [passwordHardBlock, setPasswordHardBlock] = useState(false);

    // Email verification state
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);

    const [error, setError] = useState("");
    const [showResetLink, setShowResetLink] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError("");
        setEmailNotVerified(false);
        setVerificationSent(false);

        try {
            const response = await api.post("/auth/signin", {
                email: email.toLowerCase(),
                password,
            });

            // Check for MFA requirement
            if (response.data.mfaRequired) {
                setMfaRequired(true);
                setTempUserId(response.data.userId);
                setMfaMethod(response.data.mfaMethod || 'totp');
                setMaskedPhone(response.data.maskedPhone || '');
                if (response.data.passwordChangeRequired) {
                    setPasswordChangeRequired(true);
                    setPasswordHardBlock(response.data.passwordHardBlock || false);
                }
                // Auto-trigger SMS send for SMS users
                if (response.data.mfaMethod === 'sms') {
                    try {
                        setSmsLoginSending(true);
                        await api.post('/mfa/sms/send-login', { userId: response.data.userId });
                    } catch (smsErr) {
                        setError('Could not send SMS code. Please try again.');
                    } finally {
                        setSmsLoginSending(false);
                    }
                }
                return;
            }

            // Check for MFA setup required (Company Admin without MFA)
            if (response.data.mfaSetupRequired) {
                setPendingLoginData(response.data);
                // Store token so MFA setup API calls work
                localStorage.setItem("token", response.data.token);
                setMfaSetupRequired(true);
                if (response.data.passwordChangeRequired) {
                    setPasswordChangeRequired(true);
                    setPasswordHardBlock(response.data.passwordHardBlock || false);
                }
                return;
            }

            // Check for password change prompt
            if (response.data.passwordChangeRequired) {
                setPendingLoginData(response.data);
                localStorage.setItem("token", response.data.token);
                setPasswordChangeRequired(true);
                setPasswordHardBlock(response.data.passwordHardBlock || false);
                return;
            }

            finalizeLogin(response.data);

        } catch (err) {
            const errorData = err.response?.data;
            const errorMsg = errorData?.error || "Invalid email or password.";

            if (errorData?.emailNotVerified) {
                setEmailNotVerified(true);
                setError(errorMsg);
                return;
            }

            setError(errorMsg);
            setShowResetLink(errorMsg.toLowerCase().includes("password"));
        }
    };

    const handleResendVerification = async () => {
        setResendingVerification(true);
        try {
            await api.post("/auth/resend-verification", { email: email.toLowerCase() });
            setVerificationSent(true);
        } catch (err) {
            setError(err.response?.data?.error || "Error sending verification email.");
        } finally {
            setResendingVerification(false);
        }
    };

    const handleMfaVerify = async (e) => {
        e.preventDefault();
        setError("");

        try {
            const response = await api.post("/auth/verify-login-mfa", {
                userId: tempUserId,
                token: mfaCode
            });

            if (passwordChangeRequired) {
                setPendingLoginData(response.data);
                localStorage.setItem("token", response.data.token);
                setMfaRequired(false);
                return;
            }

            finalizeLogin(response.data);

        } catch (err) {
            setError(err.response?.data?.error || "Invalid MFA Code.");
        }
    };

    const handleSmsMfaVerify = async (code) => {
        setError("");
        try {
            const response = await api.post("/auth/verify-login-mfa", {
                userId: tempUserId,
                token: code
            });
            if (passwordChangeRequired) {
                setPendingLoginData(response.data);
                localStorage.setItem("token", response.data.token);
                setMfaRequired(false);
                return;
            }
            finalizeLogin(response.data);
        } catch (err) {
            setError(err.response?.data?.error || "Invalid code. Please try again.");
        }
    };

    const handleMfaSetupComplete = () => {
        if (passwordChangeRequired) {
            setMfaSetupRequired(false);
            return;
        }
        if (pendingLoginData) {
            finalizeLogin(pendingLoginData);
        }
    };

    const handlePasswordUpdateComplete = () => {
        setPasswordChangeRequired(false);
        if (pendingLoginData) {
            finalizeLogin(pendingLoginData);
        }
    };

    const handlePasswordUpdateSkip = () => {
        setPasswordChangeRequired(false);
        if (pendingLoginData) {
            finalizeLogin(pendingLoginData);
        }
    };

    const finalizeLogin = (data) => {
        const { token, isSuperUser, userId, role } = data;
        localStorage.setItem("token", token);
        localStorage.setItem("isSuperUser", isSuperUser);
        localStorage.setItem("userId", userId);
        localStorage.setItem("role", role);

        try {
            const decodedUser = jwtDecode(token);
            localStorage.setItem("user", JSON.stringify(decodedUser));

            if (typeof onSignInSuccess === "function") {
                onSignInSuccess(decodedUser);
            }

            if (role === "Candidate" || role === "Target") {
                navigate("/assessment");
            } else {
                navigate("/employer-dashboard");
            }
        } catch (err) {
            console.error("Token decode failed", err);
            setError("Login failed during token processing.");
        }
    };

    const handleResetPassword = async () => {
        if (!email) {
            setError("Please enter your email before requesting a reset.");
            return;
        }

        setResetEmailSent(false);
        setError("");

        try {
            await api.post("/auth/request-password-reset", { email });
            setError("");
            setResetEmailSent(true);
        } catch (err) {
            setResetEmailSent(false);
            setError(err.response?.data?.error || "Error sending reset email.");
        }
    };

    const handleGoogleLogin = () => {
        // Capture UTM params to localStorage before OAuth redirect destroys URL (UTM-05)
        captureUtmToLocalStorage();
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "/api";
        window.location.href = `${baseUrl}/auth/google`;
    };

    // Determine which view to show
    const getTitle = () => {
        if (passwordChangeRequired && !mfaSetupRequired && !mfaRequired) {
            return passwordHardBlock ? "Password Update Required" : "Update Password";
        }
        if (mfaSetupRequired) return "MFA Setup Required";
        if (mfaRequired) return "Two-Factor Authentication";
        return "Sign In";
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray">
            <div className="w-full max-w-md bg-bluegray shadow-lg rounded-lg p-6">

                <h1 className="text-3xl font-bold text-center text-orange mb-6">
                    {getTitle()}
                </h1>

                {error && !emailNotVerified && (
                    <div className="bg-red text-white text-sm p-2 rounded mb-4">
                        {error.includes("Would you like to create one?") ? (
                            <>
                                No account found with this email.{" "}
                                <Link to="/candidate-signup" className="text-orange underline hover:text-deeporange">
                                    Would you like to create one?
                                </Link>
                            </>
                        ) : (
                            error
                        )}
                    </div>
                )}

                {/* Email not verified message */}
                {emailNotVerified && (
                    <div className="bg-yellow-600 text-white text-sm p-3 rounded mb-4">
                        <p className="mb-2">{error}</p>
                        {verificationSent ? (
                            <p className="text-green-300">Verification email sent! Check your inbox.</p>
                        ) : (
                            <button
                                onClick={handleResendVerification}
                                disabled={resendingVerification}
                                className="text-orange underline hover:text-deeporange font-medium"
                            >
                                {resendingVerification ? "Sending..." : "Resend verification email"}
                            </button>
                        )}
                    </div>
                )}

                {/* Password Update Prompt (non-compliant password, not locked out) */}
                {passwordChangeRequired && !mfaSetupRequired && !mfaRequired && (
                    <UpdatePassword
                        onComplete={handlePasswordUpdateComplete}
                        onSkip={passwordHardBlock ? undefined : handlePasswordUpdateSkip}
                        isHardBlock={passwordHardBlock}
                    />
                )}

                {/* MFA Setup Required (Company Admin and Recruiter without MFA) */}
                {mfaSetupRequired && !passwordChangeRequired && (
                    <div>
                        <p className="text-sm text-center mb-4 text-gray-300">
                            Your account requires multi-factor authentication for security. Please set up MFA to continue.
                        </p>
                        <MfaSetup onComplete={handleMfaSetupComplete} />
                    </div>
                )}

                {/* MFA Code Entry */}
                {mfaRequired && !passwordChangeRequired && (
                    mfaMethod === 'sms' ? (
                        <div className="space-y-4">
                            <p className="text-sm text-center mb-4">
                                {smsLoginSending ? 'Sending code...' : `Enter the 6-digit code sent to ${maskedPhone}`}
                            </p>
                            <SmsDigitInput
                                onComplete={handleSmsMfaVerify}
                                disabled={smsLoginSending}
                                error={!!error}
                            />
                            {error && <p className="text-red-500 text-sm text-center mt-2">{error}</p>}
                            <button
                                type="button"
                                onClick={() => { setMfaRequired(false); setMfaCode(""); setError(""); setPasswordChangeRequired(false); setMfaMethod('totp'); setMaskedPhone(''); }}
                                className="w-full text-sm text-lightblue hover:text-white mt-2"
                            >
                                Back to Login
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleMfaVerify} className="space-y-4">
                            <p className="text-sm text-center mb-4">
                                Please enter the 6-digit code from your authenticator app.
                            </p>
                            <div>
                                <label htmlFor="mfaCode" className="block text-sm font-medium mb-1">Authentication Code</label>
                                <input
                                    type="text"
                                    id="mfaCode"
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value)}
                                    placeholder="123456"
                                    required
                                    maxLength="6"
                                    className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange text-center text-lg tracking-widest"
                                />
                            </div>
                            <button type="submit" className="w-full py-2 px-4 bg-orange text-white font-medium rounded hover:bg-deeporange">
                                Verify Code
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMfaRequired(false); setMfaCode(""); setError(""); setPasswordChangeRequired(false); setMfaMethod('totp'); setMaskedPhone(''); }}
                                className="w-full text-sm text-lightblue hover:text-white mt-2"
                            >
                                Back to Login
                            </button>
                        </form>
                    )
                )}

                {/* Standard Login Form */}
                {!mfaRequired && !mfaSetupRequired && !passwordChangeRequired && (
                    <>
                        <form onSubmit={handleSignIn} className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium mb-1">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                                />
                            </div>
                            <button type="submit" className="w-full py-2 px-4 bg-orange text-white font-medium rounded hover:bg-deeporange">
                                Sign In
                            </button>
                        </form>

                        <div className="mt-6">
                            <div className="relative flex py-2 items-center">
                                <div className="flex-grow border-t border-gray-600"></div>
                                <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">OR</span>
                                <div className="flex-grow border-t border-gray-600"></div>
                            </div>

                            <button
                                type="button"
                                onClick={handleGoogleLogin}
                                className="w-full py-2 px-4 bg-white text-gray-900 font-medium rounded hover:bg-gray-200 transition duration-200 flex items-center justify-center gap-3 mt-2"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" />
                                    <path fill="#EA4335" d="M12 4.36c1.6 0 3.06.56 4.23 1.69l3.18-3.18C17.45 1.14 14.97 0 12 0 7.7 0 3.99 2.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Sign in with Google
                            </button>
                        </div>
                    </>
                )}

                {/* Forgot Password Link */}
                {!mfaRequired && !mfaSetupRequired && !passwordChangeRequired && showResetLink && (
                    <div className="mt-4 text-center">
                        <p className="text-sm">Forgot your password?</p>
                        <button
                            onClick={handleResetPassword}
                            disabled={resetEmailSent}
                            className={`text-orange hover:text-deeporange font-medium ${resetEmailSent ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                            {resetEmailSent ? "Sending email..." : "Reset Password"}
                        </button>
                    </div>
                )}

                {resetEmailSent && (
                    <p className="text-green-400 text-sm text-center mt-4">
                        Password reset email sent! Check your inbox.
                    </p>
                )}
            </div>
        </div>
    );
};

export default SignIn;
